/**
 * 신규 장비 관부 등록 모달 (LRM 계약 v1.1).
 *
 * LRM 이 EXIF 를 적용해 재인코딩한 registration-photo 를 그대로 띄우고, 그 이미지의 자연 크기
 * (naturalWidth/Height) 픽셀 좌표를 보낸다 — 좌표 변환 버그가 구조적으로 생기지 않는다.
 * 클릭 = 관부 지정 · 끌기 = 포기 상자 다시 그리기 (기본은 LRM 의 자동 제안 상자).
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GrowthRecord } from '@grosync/common';
import {
  detectLrmReference,
  fetchLrmRegistrationContext,
  fetchLrmRegistrationPhoto,
  submitLrmRegistration,
  type LrmRegistrationBody,
  type LrmRegistrationMarker,
} from '../../../api/growth';
import { Sym } from '../../../ui';
import { toneSurface } from './LrmRegistrationNotice';

export interface ViewBox { left: number; top: number; width: number; height: number }

/** 화면 좌표 → 사진 자연 좌표 (등록 좌표계). 소수 한 자리면 충분하다. */
export function toNatural(
  clientX: number,
  clientY: number,
  rect: ViewBox,
  natural: { width: number; height: number },
): [number, number] {
  const x = ((clientX - rect.left) / rect.width) * natural.width;
  const y = ((clientY - rect.top) / rect.height) * natural.height;
  const clamp = (v: number, max: number): number => Math.min(Math.max(v, 0), max - 1);
  return [Math.round(clamp(x, natural.width) * 10) / 10, Math.round(clamp(y, natural.height) * 10) / 10];
}

/** 끌기 두 점 → 정규화된 상자 [x1,y1,x2,y2]. 6px(자연 좌표) 미만이면 클릭으로 본다 → null */
export function dragToBox(
  a: [number, number],
  b: [number, number],
  minSide = 6,
): [number, number, number, number] | null {
  const box: [number, number, number, number] = [
    Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1]),
  ];
  if (box[2] - box[0] < minSide || box[3] - box[1] < minSide) return null;
  return box;
}

/** 기준 물체 입력 방식 — none 은 입력 안 함 (기존 측정 설정 그대로) */
export type MarkerKind = 'none' | 'aruco' | 'corners';

/** 찍은 순서와 상관없이 네 점을 TL·TR·BR·BL 로 (화면 좌표: 중심 둘레 각도 순 = 시계 방향, 시작은 x+y 최소) */
export function orderCorners(pts: [number, number][]): [number, number][] {
  const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const cw = [...pts].sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
  const start = cw.reduce((best, p, i) => (p[0] + p[1] < cw[best]![0] + cw[best]![1] ? i : best), 0);
  return [...cw.slice(start), ...cw.slice(0, start)];
}

/** 기준 물체 입력 → 등록 marker. none 이면 null (보내지 않는다). 한 변 범위는 LRM 계약과 같다 (5~1000 mm).
 *  관부 높이(face_up_mm)는 묻지 않는다 (2026-10-30 결정) — LRM 은 '기준 물체 밑변 = 흙(관부) 높이' 로 본다.
 *  현장 규칙: 기준 물체를 흙(관부) 높이에 둔다. 벽·받침 위에 두면 초장이 틀린다. */
export function buildMarker(
  kind: MarkerKind,
  sideText: string,
  shape: 'square' | 'cube',
  corners: [number, number][],
): LrmRegistrationMarker | null | { error: string } {
  if (kind === 'none') return null;
  const side = Number(sideText);
  if (!sideText.trim() || !Number.isFinite(side) || side < 5 || side > 1000) {
    return { error: '기준 물체 한 변 길이를 5~1000 mm 로 입력해 주세요' };
  }
  if (kind === 'aruco') return { side_mm: side, type: 'aruco' };
  if (corners.length !== 4) return { error: `사진에서 기준 물체 꼭짓점 4개를 찍어 주세요 (${corners.length}/4)` };
  return { side_mm: side, type: 'corners', corners_px: orderCorners(corners), shape };
}

/** 등록 요청 본문 — profile 필수 여부는 측정 설정 유무(ctx.measure_config_missing)가 정한다.
 *  marker 를 주면 profile 은 보내지 않는다 (LRM 계약: 둘을 함께 주면 422) */
/** 철사 큐브 촬영 안내 — 관부 높이는 묻지 않고 LRM 이 깊이로 자동 추정한다 (큐브가 관부 높이에 있어야 정확). */
export const CUBE_PLACEMENT_HINT = '기준 큐브는 식물의 관부(흙 표면)와 비슷한 높이에 놓아주세요.';

export function markerPlacementHint(kind: MarkerKind, shape: 'square' | 'cube'): string | null {
  return kind === 'corners' && shape === 'cube' ? CUBE_PLACEMENT_HINT : null;
}

export function buildRegistrationBody(
  crown: [number, number] | null,
  box: [number, number, number, number] | null,
  profile: string,
  name: string,
  marker: LrmRegistrationMarker | null | { error: string } = null,
): LrmRegistrationBody | { error: string } {
  if (!crown) return { error: '사진에서 관부(줄기가 배지/지면에서 나오는 지점)를 클릭해 주세요' };
  if (marker && 'error' in marker) return marker;
  return {
    crown_px: crown,
    plant_box: box,
    profile: marker ? null : profile || null,
    name: name || null,
    ...(marker ? { marker } : {}),
  };
}

interface Props {
  record: GrowthRecord;
  onClose: () => void;
  onRegistered?: () => void;
  /** 다시 등록 (처리 실패 뒤) — 등록된 장비여도 기준 물체 칸을 보인다 (LRM 이 받은 marker 로 측정 설정을 바꾼다) */
  reregister?: boolean;
}

export function LrmRegistrationModal({ record, onClose, onRegistered }: Props) {
  const queryClient = useQueryClient();
  const [crown, setCrown] = useState<[number, number] | null>(null);
  const [sideText, setSideText] = useState('');
  const [side, setSide] = useState<number | null>(null);
  const [profile, setProfile] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  // 포기 상자는 LRM 이 자동 제안한다 (사람 입력 아님) — 등록하면 측정이 '구조 마스크 ∪ 상자 SAM2 마스크' 로 포기 전체를 잡는다.
  // 상자 없이 등록하면 줄기 검출이 하나만 잡힌 날 잎 하나만 분할돼 '관부가 마스크 밖' 으로 측정이 거절됐다 (2026-11-03 E2E)
  const ctxQ = useQuery({ queryKey: ['lrm-registration', record.id],
    queryFn: () => fetchLrmRegistrationContext(record.id, true) });
  const ctx = ctxQ.data;
  const firstRegistration = ctx?.registered === false;
  useEffect(() => {
    const value = Number(sideText);
    setSide(null);
    const timer = window.setTimeout(() => {
      if (sideText.trim() && Number.isFinite(value) && value >= 5 && value <= 1000) setSide(value);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [sideText]);
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setPhotoUrl(null);
    setPhotoError(null);
    void fetchLrmRegistrationPhoto(record.id).then((blob) => {
      if (cancelled) return;
      url = URL.createObjectURL(blob);
      setPhotoUrl(url);
    }, (error: unknown) => {
      if (!cancelled) setPhotoError(error instanceof Error ? error.message : '등록 사진을 받지 못했습니다');
    });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [record.id]);
  const detectionQ = useQuery({
    queryKey: ['lrm-reference-detection', record.id, side],
    queryFn: () => detectLrmReference(record.id, side!),
    enabled: firstRegistration && side !== null,
    retry: false,
    staleTime: 60_000,
  });
  const detection = side !== null ? detectionQ.data : undefined;
  const plantBox = ctx?.suggested_plant_box ?? null;
  const body = buildRegistrationBody(crown, plantBox, profile, '', firstRegistration
    ? (side === null ? { error: '기준 물체 한 변 길이를 5~1000 mm로 입력해 주세요' }
      : { type: 'auto', reference_type: 'wire_cube', side_mm: side })
    : null);
  const mutation = useMutation({
    mutationFn: (value: LrmRegistrationBody) => submitLrmRegistration(record.id, value),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['growth'] }); onRegistered?.(); },
  });
  const canSubmit = !!ctx && !!photoUrl && !('error' in body) && !mutation.isPending
    && (!firstRegistration || (!!detection && !detectionQ.isFetching))
    && (firstRegistration || !ctx.measure_config_missing || !!profile);
  const detecting = side !== null && detectionQ.isFetching;
  return (
    <div role="dialog" aria-modal="true" aria-label="관부 등록"
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--md-sys-color-surface-container-lowest)', color: 'var(--md-sys-color-on-surface)',
                    borderRadius: 20, padding: 22, width: 'min(980px, 96vw)', maxHeight: '94vh', overflow: 'auto',
                    font: '400 14px/21px var(--gs-font-kr)', boxShadow: '0 12px 40px rgba(0,0,0,.25)' }}>
        {/* 머리 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Sym name="pin_drop" size={24} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: '700 18px/24px var(--gs-font-kr)' }}>관부 등록</div>
            <div style={{ font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
              {record.deviceId} · {record.recordedAt.slice(0, 10)} 사진
            </div>
          </div>
          <button type="button" aria-label="닫기" onClick={onClose}
                  style={{ width: 36, height: 36, borderRadius: 9999, border: 'none', cursor: 'pointer', background: 'transparent',
                           color: 'var(--md-sys-color-on-surface)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sym name="close" size={22} />
          </button>
        </div>
        {mutation.isSuccess ? (
          <div role="status" style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: '700 16px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-primary)' }}>
              <Sym name="check_circle" size={22} fill={1} /> 등록 완료
            </div>
            <p style={{ margin: 0 }}>처음 올린 사진을 자동 분석에 제출합니다. 진행 상태와 결과는 이 화면의 촬영 기록에서 볼 수 있습니다 (보통 5~10분).</p>
            {mutation.data.warnings.map((warning) => <p key={warning} style={{ margin: 0 }}>{warning}</p>)}
            {mutation.data.measure_config_missing && (
              <p role="alert" style={{ margin: 0, color: 'var(--md-sys-color-error)' }}>{mutation.data.measure_config_missing}</p>
            )}
            <PrimaryBtn onClick={onClose}>확인</PrimaryBtn>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* 사진 — 클릭 = 관부 */}
            <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0, borderRadius: 12,
                          overflow: 'hidden', background: 'var(--md-sys-color-surface-container)', flex: '0 1 auto' }}>
              {photoUrl && ctx ? (
                <img src={photoUrl} alt="등록 사진" draggable={false}
                     onClick={(e) => setCrown(toNatural(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), ctx.photo))}
                     style={{ display: 'block', maxWidth: '100%', maxHeight: '68vh', cursor: 'crosshair' }} />
              ) : (
                <p role={photoError || ctxQ.isError ? 'alert' : 'status'}
                   style={{ lineHeight: 1.5, padding: 24, margin: 0, minWidth: 280,
                            color: photoError || ctxQ.isError ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-on-surface)' }}>
                  {photoError || (ctxQ.error as Error | null)?.message || '등록 사진을 불러오는 중…'}
                </p>
              )}
              {ctx && photoUrl && (
                <svg viewBox={`0 0 ${ctx.photo.width} ${ctx.photo.height}`} preserveAspectRatio="none"
                     aria-label="자동 인식한 기준 물체와 관부"
                     style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                  {plantBox && <rect x={plantBox[0]} y={plantBox[1]} width={plantBox[2] - plantBox[0]} height={plantBox[3] - plantBox[1]}
                    fill="none" stroke="#ffb300" strokeWidth="2" strokeDasharray="8 6" vectorEffect="non-scaling-stroke" />}
                  {detection?.vertices_px && detection.edges?.map(([a, b]) => (
                    <line key={`${a}-${b}`} x1={detection.vertices_px![a]![0]} y1={detection.vertices_px![a]![1]}
                          x2={detection.vertices_px![b]![0]} y2={detection.vertices_px![b]![1]}
                          stroke="#00e676" strokeOpacity="0.55" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                  ))}
                  {detection && <polygon points={detection.corners_px.map((q) => q.join(',')).join(' ')}
                    fill="none" stroke="#00e676" strokeWidth="3" vectorEffect="non-scaling-stroke" />}
                  {crown && <circle cx={crown[0]} cy={crown[1]} r={ctx.photo.width / 80} fill="#f44336" stroke="white"
                    strokeWidth="2" vectorEffect="non-scaling-stroke" />}
                </svg>
              )}
            </div>
            {/* 단계 */}
            <div style={{ flex: '1 1 300px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <Step n={1} done={!!crown} title="관부 클릭">
                <span>사진에서 줄기가 흙(배지)에서 나오는 지점을 한 번 클릭하세요.</span>
                <StatusLine tone={crown ? 'ok' : 'idle'} icon={crown ? 'check_circle' : 'touch_app'}>
                  {crown ? '관부 선택 완료' : '아직 선택하지 않았습니다'}
                </StatusLine>
                {plantBox ? (
                  <span style={{ color: 'var(--md-sys-color-on-surface-variant)', font: '500 12px/17px var(--gs-font-kr)' }}>
                    노란 점선 = 자동으로 잡은 포기 범위
                  </span>
                ) : ctxQ.isFetching ? (
                  <span style={{ color: 'var(--md-sys-color-on-surface-variant)', font: '500 12px/17px var(--gs-font-kr)' }}>
                    포기 범위 찾는 중…
                  </span>
                ) : null}
              </Step>
              {firstRegistration && (
                <Step n={2} done={!!detection && !detecting} title="기준 큐브 한 변 길이">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input aria-label="기준 큐브 한 변 길이 (mm)" type="number" min={5} max={1000} step="any" value={sideText}
                           placeholder="예: 100" onChange={(e) => setSideText(e.target.value)}
                           style={{ width: 130, height: 40, padding: '0 12px', borderRadius: 10, outline: 'none',
                                    border: '1px solid var(--md-sys-color-outline)', background: 'var(--md-sys-color-surface)',
                                    color: 'var(--md-sys-color-on-surface)', font: '600 15px/1 var(--gs-font-kr)' }} />
                    <span style={{ font: '600 14px/1 var(--gs-font-kr)' }}>mm</span>
                  </label>
                  <div role="note" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 12,
                                            ...toneSurface('var(--gs-warning)'), color: 'var(--md-sys-color-on-surface)',
                                            font: '500 13px/19px var(--gs-font-kr)' }}>
                    <Sym name="info" size={18} fill={1} style={{ color: 'var(--gs-warning)' }} />
                    <span>{CUBE_PLACEMENT_HINT}</span>
                  </div>
                  {detecting && (
                    <StatusLine tone="idle" icon="progress_activity" role="status">기준 물체를 자동 인식하는 중…</StatusLine>
                  )}
                  {detection && !detectionQ.isFetching && (
                    <StatusLine tone="ok" icon="check_circle" role="status">
                      기준 물체 자동 인식 완료 — 초록 틀이 큐브와 맞는지 확인해 주세요 (굵은 선 = 앞면)
                    </StatusLine>
                  )}
                  {side !== null && detectionQ.isError && (
                    <StatusLine tone="error" icon="error" role="alert">
                      기준 물체를 자동으로 인식하지 못했습니다. 기준 물체 전체가 잘 보이도록 다시 촬영해주세요.
                      <br />{(detectionQ.error as Error).message}
                    </StatusLine>
                  )}
                </Step>
              )}
              {!firstRegistration && ctx?.measure_config_missing && (
                <Step n={2} done={!!profile} title="기존 측정 설정">
                  <select aria-label="기존 측정 설정" value={profile} onChange={(e) => setProfile(e.target.value)}
                          style={{ height: 40, padding: '0 12px', borderRadius: 10, border: '1px solid var(--md-sys-color-outline)',
                                   background: 'var(--md-sys-color-surface)', color: 'var(--md-sys-color-on-surface)',
                                   font: '600 14px/1 var(--gs-font-kr)' }}>
                    <option value="">설정 선택</option>
                    {ctx.profiles.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Step>
              )}
              <PrimaryBtn disabled={!canSubmit} onClick={() => { if (canSubmit && !('error' in body)) mutation.mutate(body); }}>
                {mutation.isPending ? '등록 및 분석 준비 중…' : '등록'}
              </PrimaryBtn>
              {mutation.isError && (
                <StatusLine tone="error" icon="error" role="alert">등록 실패: {(mutation.error as Error).message}</StatusLine>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- 화면 조각 — 관리자 디자인 토큰 (on-surface 본문 · 알약 버튼) ---- */

function Step({ n, title, done, children }: { n: number; title: string; done: boolean; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', gap: 12 }}>
      <span aria-hidden style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 9999, display: 'inline-flex', alignItems: 'center',
                                 justifyContent: 'center', font: '700 13px/1 var(--gs-font-kr)',
                                 background: done ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-secondary-container)',
                                 color: done ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-secondary-container)' }}>
        {done ? <Sym name="check" size={16} /> : n}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, flex: 1 }}>
        <div style={{ font: '700 15px/22px var(--gs-font-kr)' }}>{title}</div>
        {children}
      </div>
    </section>
  );
}

const LINE_TONE = {
  ok: 'var(--gs-success)',
  error: 'var(--md-sys-color-error)',
  idle: 'var(--md-sys-color-on-surface)',
} as const;

function StatusLine({ tone, icon, role, children }: {
  tone: keyof typeof LINE_TONE; icon: string; role?: string; children: React.ReactNode;
}) {
  return (
    <div role={role} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', color: LINE_TONE[tone],
                              font: '600 13px/19px var(--gs-font-kr)' }}>
      <Sym name={icon} size={18} fill={tone === 'idle' ? 0 : 1} />
      <span>{children}</span>
    </div>
  );
}

function PrimaryBtn({ disabled, onClick, children }: { disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
            style={{ height: 42, padding: '0 22px', borderRadius: 9999, border: 'none', alignSelf: 'flex-start',
                     cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
                     background: 'var(--md-sys-color-primary)', color: 'var(--md-sys-color-on-primary)',
                     font: '700 14px/1 var(--gs-font-kr)' }}>
      {children}
    </button>
  );
}
